import { useEffect, useState } from "react";
import { StyleSheet, Text, View } from "react-native";
import { useTranslation } from "react-i18next";
import {
  FeedbackKind,
  isApiErrorLike,
  type FeedbackKind as FeedbackKindT,
  type RateLimitCheck,
} from "@heraion/shared";

import { Button } from "../primitives/Button";
import { Chip } from "../primitives/Chip";
import { Dialog, DialogScroll } from "../primitives/Dialog";
import { Sheet } from "../primitives/Sheet";
import { TextField } from "../primitives/TextField";
import type { DurableWriteResult } from "../seams";
import type { UiTheme } from "../theme";
import { useBreakpoint } from "../useBreakpoint";

// THE feedback form, shared by mobile (Settings -> Feedback) and the web
// console (Settings -> About -> Feedback). Kind, text, send, the sent state and the
// messages live here once; what is platform is injected by the mount:
//   submit         builds the envelope (surface, version, device, last screen)
//                  and sends it through the host's durableWrite;
//   checkRateLimit the client-side ceiling (feedbackRateLimit in @heraion/shared), host-owned storage;
//   bottomInset    safe-area inset for the phone sheet (web passes none).
// Wide shows a centred Dialog, narrow a bottom Sheet.

// "crash" is the automatic reporter's kind (OP-33), not a person's choice.
const KINDS: ReadonlyArray<FeedbackKindT> = FeedbackKind.options.filter(
  (k) => k !== "crash",
);

const MAX_LEN = 2000;

export type FeedbackFormProps = {
  theme: UiTheme;
  visible: boolean;
  onClose: () => void;
  submit: (input: {
    kind: FeedbackKindT;
    body: string;
  }) => Promise<DurableWriteResult>;
  checkRateLimit: () => Promise<RateLimitCheck>;
  bottomInset?: number;
};

export function FeedbackForm({
  theme,
  visible,
  onClose,
  submit,
  checkRateLimit,
  bottomInset,
}: FeedbackFormProps) {
  const { t } = useTranslation();
  const { wide } = useBreakpoint();
  const [kind, setKind] = useState<FeedbackKindT>("bug");
  const [body, setBody] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  // Reset on each open so a stale draft from a previous opening doesn't
  // reappear.
  useEffect(() => {
    if (visible) {
      setKind("bug");
      setBody("");
      setError(null);
      setSent(false);
    }
  }, [visible]);

  const onSend = async () => {
    if (submitting) return;
    setError(null);
    const trimmed = body.trim();
    // Send is disabled on an empty field, so an empty body never gets here.
    setSubmitting(true);
    try {
      const limit = await checkRateLimit();
      if (!limit.allowed) {
        setError(t("feedback.errRateLimit"));
        return;
      }
      // Queued counts as sent (DX-2/E): a report written on bad wifi must not
      // evaporate because the report itself could not reach the server.
      const result = await submit({ kind, body: trimmed });
      if (result.outcome === "rejected") throw result.error;
      setSent(true);
    } catch (e) {
      setError(
        t(
          isApiErrorLike(e) && e.status === 429
            ? "feedback.errRateLimit"
            : "feedback.errSubmit",
        ),
      );
    } finally {
      setSubmitting(false);
    }
  };

  const styles = makeStyles(theme);
  const close = () => {
    if (!submitting) onClose();
  };
  const empty = body.trim().length === 0;

  const content = (
    <DialogScroll
      contentContainerStyle={styles.body}
      keyboardShouldPersistTaps="handled"
    >
      {sent ? (
        <View style={styles.sentBox}>
          <Text accessibilityLiveRegion="polite" role="status" style={styles.sentTitle}>{t("feedback.sentFlash")}</Text>
          <Text style={styles.sentHint}>{t("feedback.sentHint")}</Text>
          <Button
            theme={theme}
            variant="secondary"
            label={t("feedback.close")}
            onPress={onClose}
          />
        </View>
      ) : (
        <>
          <Text style={styles.help}>{t("feedback.help")}</Text>
          <Text style={styles.label}>{t("feedback.kindLabel")}</Text>
          <View accessibilityRole="radiogroup" style={styles.kindRow}>
            {KINDS.map((k) => (
              <Chip
                key={k}
                theme={theme}
                role="radio"
                hitSlop={{ top: 8, bottom: 8 }}
                label={t(`feedback.kind_${k}`)}
                selected={k === kind}
                onPress={() => setKind(k)}
              />
            ))}
          </View>

          <Text style={styles.label}>{t("feedback.bodyLabel")}</Text>
          <TextField
            theme={theme}
            multiline
            minRows={6}
            accessibilityLabel={t("feedback.bodyLabel")}
            placeholder={t("feedback.bodyPlaceholder")}
            value={body}
            onChangeText={setBody}
            maxLength={MAX_LEN}
            editable={!submitting}
          />
          <Text style={styles.counter}>{`${body.length}/${MAX_LEN}`}</Text>

          {error !== null ? (
            <Text accessibilityRole="alert" style={styles.error}>
              {error}
            </Text>
          ) : null}

          <Button
            theme={theme}
            full
            label={t(submitting ? "feedback.sending" : "feedback.send")}
            loading={submitting}
            disabled={empty}
            onPress={onSend}
          />
        </>
      )}
    </DialogScroll>
  );

  return wide ? (
    <Dialog
      theme={theme}
      visible={visible}
      onRequestClose={close}
      dismissOnBackdropPress={false}
      title={t("feedback.title")}
      closeLabel={t("feedback.close")}
      maxWidth={560}
    >
      {content}
    </Dialog>
  ) : (
    <Sheet
      theme={theme}
      visible={visible}
      onRequestClose={close}
      title={t("feedback.title")}
      onClose={close}
      closeAccessibilityLabel={t("feedback.close")}
      bottomInset={bottomInset}
    >
      {content}
    </Sheet>
  );
}

function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s, radius: r, typography: t } = theme;
  return StyleSheet.create({
    body: { gap: s.s3, paddingBottom: s.s3 },
    help: { color: p.ink3, fontSize: t.sizes.sm },
    label: {
      color: p.ink3,
      fontSize: t.sizes.sm,
      fontWeight: "700",
      marginTop: s.s3,
    },
    kindRow: { flexDirection: "row", flexWrap: "wrap", gap: s.s2 },
    counter: { color: p.ink3, fontSize: t.sizes.sm, textAlign: "right" },
    error: { color: p.danger, fontSize: t.sizes.sm, textAlign: "center" },
    sentBox: {
      padding: s.s5,
      borderRadius: r.r3,
      backgroundColor: p.surface,
      gap: s.s3,
    },
    sentTitle: { color: p.ink, fontSize: t.sizes.lg, fontWeight: "700" },
    sentHint: { color: p.ink3, fontSize: t.sizes.sm },
  });
}
